import React, { useState, useMemo, useEffect } from 'react'
import Page from '../../components/layout/Page'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Icon from '../../components/AppIcon'
import MetalRowInput from './components/MetalRowInput'
import PriceDisplay from './components/PriceDisplay'
import CalculationResults from './components/CalculationResults'
import QuoteTimer from './components/QuoteTimer'
import TransactionFlow from './components/TransactionFlow'
import ExchangeAdminSection from './components/ExchangeAdminSection'
import ExchangeTransactions from './components/ExchangeTransactions'
import ExchangeSettings from './components/ExchangeSettings'
import ExchangeDashboard from './components/ExchangeDashboard'
import ExchangeEnvelopes from './components/ExchangeEnvelopes'
import ExchangeReports from './components/ExchangeReports'
import ExchangeContractTemplate from './components/ExchangeContractTemplate'
import ExchangeAuditLog from './components/ExchangeAuditLog'
import OperatorsPanel from '../../components/control/OperatorsPanel'
import { useMetalPrices } from './hooks/useMetalPrices'
import { useIsDesktop } from './hooks/useIsDesktop'
import { db } from '../../firebase'
import { doc, getDoc } from 'firebase/firestore'
import OfficeWorkspaceShell from '../../components/layout/OfficeWorkspaceShell'
import { EXCHANGE_DESK_SECTIONS } from './exchangeNav'
import { useRole } from '../../lib/roles'
import { useAppConfig } from '../../lib/AppConfigContext'
import { useOperator } from '../../lib/OperatorContext'
import { getGoldItemErrors } from './lib/goldItemTypes.mjs'

// Deságio model (matches the shop app): two buckets — jewelry vs coin/bar.
// Real production values live in gold_settings/config (jewelry 30% / coin-bar 20%).
const DEFAULT_DESAGIO = { jewelry: 30, coinBar: 20 }
const COIN_BAR_TYPES = new Set(['coin', 'bar'])
const QUOTE_DURATION_MS = 15 * 60 * 1000
// Navigation only. The report callable independently verifies the typed PIN.
const REPORT_MANAGER_OPERATOR_IDS = new Set(['legacy_2', 'legacy_4'])

const getDesagioRate = (itemType, rates) =>
  COIN_BAR_TYPES.has(itemType) ? rates.coinBar : rates.jewelry

const formatUsd = (value) => {
  const num = Number(value)
  if (value == null || value === '' || isNaN(num)) return '$0.00'
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const ExchangeDesk = () => {
  const { config: appConfig } = useAppConfig()
  const brandName = appConfig?.branding?.shortName || appConfig?.branding?.appName || 'GoldBuy'
  const { prices, loading: pricesLoading, error: pricesError, refresh } = useMetalPrices()
  const isDesktop = useIsDesktop()
  const { role } = useRole()
  const { operator } = useOperator()
  // Admin gate (device model): admin sections require desktop + privilege.
  // For now privilege = admin role (master emails); operator is_admin joins once PINs are live.
  const canAdmin = isDesktop && role === 'admin'
  const canViewEnvelopes = isDesktop && (role === 'admin' || REPORT_MANAGER_OPERATOR_IDS.has(operator?.operatorId))

  const sections = useMemo(
    () => EXCHANGE_DESK_SECTIONS.filter((s) => !s.admin || canAdmin || (s.id === 'envelopes' && canViewEnvelopes)),
    [canAdmin, canViewEnvelopes]
  )
  const [activeId, setActiveId] = useState('buy')
  // If admin access is lost (e.g. window shrinks), fall back to Buy.
  useEffect(() => {
    if (!sections.some((s) => s.id === activeId)) setActiveId('buy')
  }, [sections, activeId])

  const activeSection = sections.find((s) => s.id === activeId) || sections[0]
  const operations = useMemo(
    () => sections.map((s) => ({ ...s, onClick: () => setActiveId(s.id) })),
    [sections]
  )

  const [items, setItems] = useState([
    { id: 1, metal: 'gold', karatWhole: '18', karatDecimal: '00', weight: '', itemType: '' }
  ])
  const [desagioRates, setDesagioRates] = useState(DEFAULT_DESAGIO)
  const [quoteTime, setQuoteTime] = useState(null)
  const [showTransaction, setShowTransaction] = useState(false)
  const [showItemErrors, setShowItemErrors] = useState(false)
  const itemErrors = getGoldItemErrors(items)

  // Load deságio (2-bucket) from gold_settings/config
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const cfg = await getDoc(doc(db, 'gold_settings', 'config'))
        if (cfg.exists()) {
          const d = cfg.data()
          setDesagioRates({
            jewelry: Number(d.desagioPercent ?? DEFAULT_DESAGIO.jewelry),
            coinBar: Number(d.desagioCoinBarPercent ?? DEFAULT_DESAGIO.coinBar),
          })
        }
      } catch (err) {
        console.warn('Could not load deságio config, using defaults')
      }
    }
    loadSettings()
  }, [])

  // Calculate totals
  const calculations = useMemo(() => {
    if (!prices?.gold) return null

    const results = items.map(item => {
      const weight = parseFloat(item.weight) || 0
      if (weight <= 0) return null

      // Calculate purity from karat (e.g., 18.50K = 18.50/24 = 0.7708)
      const karatValue = parseFloat(item.karatWhole || '18') + parseFloat(item.karatDecimal || '0') / 100
      const purity = item.metal === 'gold' ? karatValue / 24 : 0.999 // Silver is 999
      const pureWeight = weight * purity
      const basePrice = item.metal === 'gold' ? prices.gold : prices.silver
      const grossValue = pureWeight * basePrice

      // Get desagio rate for this item type
      const desagio = getDesagioRate(item.itemType, desagioRates)
      const desagioAmount = grossValue * (desagio / 100)
      const netValue = grossValue - desagioAmount

      return {
        ...item,
        karatValue,
        purity,
        pureWeight,
        basePrice,
        grossValue,
        desagio,
        desagioAmount,
        netValue
      }
    }).filter(Boolean)

    const totalWeight = results.reduce((sum, r) => sum + parseFloat(r.weight), 0)
    const totalPureWeight = results.reduce((sum, r) => sum + r.pureWeight, 0)
    const totalGross = results.reduce((sum, r) => sum + r.grossValue, 0)
    const totalDesagio = results.reduce((sum, r) => sum + r.desagioAmount, 0)
    const totalNet = results.reduce((sum, r) => sum + r.netValue, 0)

    return {
      items: results,
      totalWeight,
      totalPureWeight,
      totalGross,
      totalDesagio,
      totalNet
    }
  }, [items, prices, desagioRates])

  const addItem = () => {
    setItems(prev => [...prev, {
      id: Date.now(),
      metal: 'gold',
      karatWhole: '18',
      karatDecimal: '00',
      weight: '',
      itemType: ''
    }])
  }

  // Patch-object updates so one user action can change several fields at once
  // (e.g. picking 24K also locks the decimal to .00) without racing setState.
  const updateItem = (id, patch) => {
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, ...patch } : item
    ))
  }

  const removeItem = (id) => {
    setItems(prev => (prev.length > 1 ? prev.filter(item => item.id !== id) : prev))
  }

  const generateQuote = () => {
    setQuoteTime(Date.now())
  }

  const onQuoteExpired = () => {
    setQuoteTime(null)
  }

  const clearAll = () => {
    setItems([{ id: Date.now(), metal: 'gold', karatWhole: '18', karatDecimal: '00', weight: '', itemType: '' }])
    setQuoteTime(null)
    setShowTransaction(false)
    setShowItemErrors(false)
  }

  const startTransaction = () => {
    if (itemErrors.length) {
      setShowItemErrors(true)
      const firstError = itemErrors[0]
      document.getElementById(`gold-item-${firstError.field}-${firstError.itemId}`)?.focus()
      return
    }
    if (calculations?.totalNet) {
      setShowTransaction(true)
    }
  }

  const handleTransactionComplete = () => {
    clearAll()
  }

  const handleTransactionCancel = () => {
    setShowTransaction(false)
  }

  // Print a customer-facing quote slip via the device print dialog.
  // All interpolated values are numbers or fixed select options — no free text.
  const printQuote = () => {
    if (!calculations?.items?.length) return
    const w = window.open('', '_blank', 'noopener,noreferrer,width=440,height=680')
    if (!w) return

    const rows = calculations.items.map((it, i) => {
      const karat = it.metal === 'silver' ? 'Silver 99%' : `${it.karatWhole}.${it.karatDecimal}K Gold`
      return `<tr><td>${i + 1}. ${karat} ${it.itemType || ''} — ${it.weight}g</td><td class="r">${formatUsd(it.netValue)}</td></tr>`
    }).join('')

    w.document.write(`<!doctype html><html><head><title>${brandName} Quote</title><style>
      body{font-family:system-ui,sans-serif;padding:24px;max-width:420px;margin:0 auto;color:#17191D}
      h1{font-size:18px;margin:0 0 4px}
      .small{color:#656C78;font-size:12px;margin:2px 0}
      table{width:100%;border-collapse:collapse;font-size:14px;margin-top:14px}
      td{padding:7px 0;border-bottom:1px solid #ddd}
      td.r{text-align:right;white-space:nowrap}
      tfoot td{font-weight:700;font-size:16px;border-bottom:none;padding-top:12px}
    </style></head><body>
      <h1>${brandName} — Purchase Quote</h1>
      <p class="small">${new Date().toLocaleString()}</p>
      <p class="small">Gold ${formatUsd(prices?.gold)}/g · quote valid 15 minutes</p>
      <table><tbody>${rows}</tbody>
      <tfoot><tr><td>Payout</td><td class="r">${formatUsd(calculations.totalNet)}</td></tr></tfoot></table>
      <script>window.print()</script>
    </body></html>`)
    w.document.close()
  }

  const isBuy = activeId === 'buy'
  const hasTotal = !!calculations?.totalNet

  const resultsBlock = (
    <>
      {quoteTime && (
        <QuoteTimer
          startTime={quoteTime}
          duration={QUOTE_DURATION_MS}
          onExpired={onQuoteExpired}
        />
      )}

      <CalculationResults
        calculations={calculations}
        onGenerateQuote={generateQuote}
        quoteActive={!!quoteTime}
      />
    </>
  )

  const buyContent = (
    <div className="space-y-5">
      <PriceDisplay
        prices={prices}
        loading={pricesLoading}
        error={pricesError}
        onRefresh={refresh}
      />

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_300px] 2xl:grid-cols-[minmax(0,1fr)_340px]">
      {/* Main Calculator */}
      <div className="min-w-0 space-y-4">
        <Card variant="solid" padding="none" className="rounded-xl border-[#DCDFE5] bg-white shadow-none">
          <div className="flex items-center justify-between gap-3 border-b border-[#DCDFE5] px-4 py-4 sm:px-5">
            <h2 className="text-sm font-semibold text-[#17191D]">Items</h2>
            <span className="text-xs text-[#656C78] tabular-nums">{items.length} {items.length === 1 ? 'item' : 'items'}</span>
          </div>

          <div className="divide-y divide-[#DCDFE5]">
            {items.map((item, index) => (
              <MetalRowInput
                key={item.id}
                item={item}
                index={index}
                onUpdate={(patch) => updateItem(item.id, patch)}
                onRemove={() => removeItem(item.id)}
                canRemove={items.length > 1}
                itemErrors={showItemErrors ? Object.fromEntries(itemErrors.filter(error => error.index === index).map(error => [error.field, error.message])) : undefined}
              />
            ))}
          </div>

          {/* Below the list (add-row pattern): the next tap target is right
              where the eye lands after filling the last item. */}
          <div className="border-t border-[#DCDFE5] px-4 py-4 sm:px-5">
            <Button
              variant="outline"
              onClick={addItem}
              iconName="Plus"
              iconPosition="left"
              className="w-full border-[#DCDFE5] bg-white text-[#17191D] hover:bg-[#F5F6F8] sm:w-auto"
            >
              Add Item
            </Button>
          </div>
        </Card>

        {/* Mobile: results right below the items, single column */}
        <div className="space-y-4 lg:hidden">
          {resultsBlock}
          <Button
            variant="outline"
            className="w-full"
            disabled={!hasTotal}
            onClick={printQuote}
            iconName="Printer"
            iconPosition="left"
          >
            Print Quote
          </Button>
        </div>

        {/* Deságio rates are deliberately NOT shown here: the buy screen (and
            the printed quote) face the customer. Rates live in Exchange Desk →
            Settings; margins show up only in the internal reports. */}
      </div>

      {/* Desktop sidebar — results & actions */}
      <div className="hidden min-w-0 space-y-4 lg:sticky lg:top-24 lg:block">
        {resultsBlock}

        <div className="space-y-2">
          <Button
            variant="default"
            className="w-full bg-[#17191D] text-white hover:bg-[#292D34]"
            disabled={!hasTotal}
            onClick={startTransaction}
            iconName="FileText"
            iconPosition="left"
          >
            Create Transaction
          </Button>
          <Button
            variant="outline"
            className="w-full"
            disabled={!hasTotal}
            onClick={printQuote}
            iconName="Printer"
            iconPosition="left"
          >
            Print Quote
          </Button>
        </div>
      </div>
      </div>
    </div>
  )

  const sectionContent = isBuy
    ? buyContent
    : activeId === 'dashboard'
      ? <ExchangeDashboard onNavigate={setActiveId} />
      : activeId === 'transactions'
        ? <ExchangeTransactions />
        : activeId === 'envelopes'
          ? <ExchangeEnvelopes canClaimEnvelopes={role === 'admin'} />
          : activeId === 'reports'
            ? <ExchangeReports />
            : activeId === 'settings'
              ? <ExchangeSettings />
              : activeId === 'contract'
                ? <ExchangeContractTemplate />
                : activeId === 'operators'
                  ? <OperatorsPanel />
                  : activeId === 'audit'
                    ? <ExchangeAuditLog />
                    : <ExchangeAdminSection section={activeSection} />

  return (
    <Page
      size="full"
      title="Exchange Desk"
      subtitle={isBuy ? 'Gold & silver buying calculator' : activeSection?.description}
      // Leave room for the mobile action bar (+ the portal bottom nav below md)
      // so neither ever covers content.
      className={isBuy ? 'pb-44 md:pb-28 lg:pb-6' : undefined}
      actions={isBuy ? (
        <>
          <Button variant="ghost" size="sm" onClick={clearAll} iconName="RotateCcw" iconPosition="left">
            Clear
          </Button>
          <Button variant="outline" size="sm" onClick={refresh} disabled={pricesLoading} iconName="RefreshCw" iconPosition="left">
            Refresh Prices
          </Button>
        </>
      ) : null}
    >
      {isDesktop ? (
        // Desktop: office workspace with the module's own menu (Buy / Admin sections).
        <OfficeWorkspaceShell
          activeSectionId={activeSection?.id}
          activeSectionLabel={activeSection?.label}
          activeSectionIcon={activeSection?.icon}
          activeSectionDescription={activeSection?.description}
          operations={operations}
          workspaces={[]}
          operationsTitle="Exchange Desk"
          compactMenu
          layoutPreset="office"
        >
          {sectionContent}
        </OfficeWorkspaceShell>
      ) : (
        // Mobile: Buy AU only (like the shop tablets on port 8000) — no office
        // chrome, just the calculator, full-width.
        sectionContent
      )}

      {/* Keep the payout action above mobile/tablet navigation and its safe area. */}
      {isBuy && !showTransaction && hasTotal ? (
        <div className="portal-action-bar lg:hidden fixed inset-x-0 z-40 border-t border-[#DCDFE5] bg-white px-4 py-3">
          <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-xs leading-none text-[#656C78]">Payout</p>
              <p className="break-words text-xl font-semibold tracking-tight text-[#17191D] tabular-nums">{formatUsd(calculations.totalNet)}</p>
            </div>
            <Button
              variant="default"
              size="lg"
              className="min-w-[180px] flex-1 bg-[#17191D] px-3 text-sm text-white hover:bg-[#292D34]"
              onClick={startTransaction}
              iconName="FileText"
              iconPosition="left"
            >
              Create Transaction
            </Button>
          </div>
        </div>
      ) : null}

      {/* Transaction takes over the screen in stages (like the original app) */}
      {showTransaction && (
        <TransactionFlow
          calculations={calculations}
          prices={prices}
          items={items}
          onComplete={handleTransactionComplete}
          onCancel={handleTransactionCancel}
        />
      )}
    </Page>
  )
}

export default ExchangeDesk
